import { LayoutEngine } from '@src/editor/models/layout/LayoutEngine';
import { resolveTargetAspectRatio } from '@src/hooks/useReframeSpeakersTab';
import { planReframeAfterFaceMapChange } from '@src/services/framing/planReframeAfterFaceMapChange';
import { SmartFramingService } from '@src/services/framing/SmartFramingService';
import { SceneService } from '@src/services/SceneService';
import { useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { AspectRatio } from '@src/types.ts/video';
import { LogManager } from '@src/utility/Logger';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import type { IVideoEditor } from '@src/editor/interface/IVideoEditor';
import type { FaceMapSnapshot } from '@src/services/framing/planReframeAfterFaceMapChange';
import type { FaceDetection } from '@src/services/framing/types';

const logger = LogManager.getLogger('feature:useReframeOnFaceMapChange');

export interface ReframeOnFaceMapChange {
  /**
   * Call right before an action that changes who each face is. The open
   * version reframes once that action's face data arrives.
   *
   * @param faceDataReplaced The action regenerates the face detections
   *   themselves (Force Re-run), not only the mapping.
   */
  expectChange(faceDataReplaced: boolean): void;
  /** The action failed; nothing new will arrive. */
  cancel(): void;
}

interface PendingChange {
  faceDataReplaced: boolean;
  /** The map identity before the action; a new one means it was rebuilt. */
  mapBefore: Record<string, string[]>;
  before: FaceMapSnapshot;
}

/**
 * Reframes the open 9:16 / 1:1 version after Face Mapping is saved or a face
 * re-run finishes (PLAT-12144), as the reframe modal's Speakers tab does
 * after a pick. `speakerLabelToFaceLabels` is the map `useSpeakerFaceMapping`
 * rebuilds whenever the face data is refetched.
 */
export function useReframeOnFaceMapChange({
  editor,
  storyId,
  faceDetections,
  speakerLabelToFaceLabels,
  detecting,
}: {
  editor: IVideoEditor | undefined;
  storyId: string | undefined;
  faceDetections: FaceDetection[] | null;
  speakerLabelToFaceLabels: Record<string, string[]>;
  detecting: boolean;
}): ReframeOnFaceMapChange {
  const rootStore = useStore();
  const { speakerFaceMapStore } = rootStore;
  const videoCreator = useVideoCreatorStore();
  const pending = useRef<PendingChange | null>(null);

  const framer = useMemo(
    () =>
      editor
        ? new SmartFramingService(
            editor,
            new SceneService(),
            new LayoutEngine(),
          )
        : null,
    [editor],
  );

  const expectChange = useCallback(
    (faceDataReplaced: boolean) => {
      if (!editor || !faceDetections?.length) return;
      pending.current = {
        faceDataReplaced,
        mapBefore: speakerLabelToFaceLabels,
        before: {
          // Real or guessed — whatever the scenes were framed with.
          speakerMap:
            speakerFaceMapStore.mapForFraming(editor.getOriginalTranscript()) ??
            {},
          faceDetections,
        },
      };
    },
    [editor, faceDetections, speakerLabelToFaceLabels, speakerFaceMapStore],
  );

  const cancel = useCallback(() => {
    pending.current = null;
  }, []);

  useEffect(() => {
    const change = pending.current;
    if (!change || !storyId || !editor || !framer || !faceDetections) return;
    // Wait for the refetched face data to rebuild the map.
    if (speakerLabelToFaceLabels === change.mapBefore) return;
    if (change.faceDataReplaced && detecting) return;
    pending.current = null;

    const aspectRatio = videoCreator.getCurrentAspectRatio();
    if (
      rootStore.featureFlagsStore.flags.autoFramingStrategy === 'basic' ||
      (aspectRatio !== AspectRatio.AR_9_16 &&
        aspectRatio !== AspectRatio.AR_1_1)
    ) {
      return;
    }

    // `useSpeakerFaceMapping` only pushes a non-empty map to the store; an
    // emptied mapping must reach it too, or framing keeps the old faces.
    if (Object.keys(speakerLabelToFaceLabels).length === 0) {
      speakerFaceMapStore.set(storyId, {});
    }
    if (change.faceDataReplaced) {
      speakerFaceMapStore.setFaceDetections(storyId, faceDetections);
    }

    const sceneUpdates = planReframeAfterFaceMapChange({
      framer,
      before: change.before,
      after: {
        speakerMap:
          speakerFaceMapStore.mapForFraming(editor.getOriginalTranscript()) ??
          {},
        faceDetections,
      },
      faceDataReplaced: change.faceDataReplaced,
      targetAspectRatio: resolveTargetAspectRatio(aspectRatio),
    });
    const updated =
      rootStore.editorStore.rendererSyncService?.commitSceneUpdates(
        sceneUpdates,
      );
    const count = updated?.size ?? 0;
    logger.info('Reframed scenes after a face mapping change', {
      faceDataReplaced: change.faceDataReplaced,
      reframedScenes: count,
    });
    if (count > 0) {
      videoCreator.showToast({
        state: 'success',
        message: `Reframed ${count} ${count === 1 ? 'scene' : 'scenes'} to match Face Mapping`,
      });
    }
  }, [
    storyId,
    editor,
    framer,
    faceDetections,
    speakerLabelToFaceLabels,
    detecting,
    videoCreator,
    rootStore,
    speakerFaceMapStore,
  ]);

  return useMemo(() => ({ expectChange, cancel }), [expectChange, cancel]);
}
